import { useCallback, useState } from "react";

// 選択可能なモデルの一覧。
//
// 並び順は DebugOverlay の <select> 表示順と一致する。
// モデルリストを変更した場合は DebugOverlay.tsx の MODEL_PRICING テーブルも合わせて更新すること。
export const AVAILABLE_MODELS = [
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.5",
  "gpt-5.4-nano",
  "gpt-5.4-mini",
  "gpt-5.4",
  "gpt-4.1-mini",
  "gpt-4o-mini",
] as const;

export type AvailableModel = (typeof AVAILABLE_MODELS)[number];

// localStorage への保存キー（palimia 名前空間で統一）
const MODEL_STORAGE_KEY = "palimia.model";

const DEFAULT_MODEL: AvailableModel = "gpt-5.6-sol";

export type UseModelSelectorResult = {
  selectedModel: AvailableModel;
  setModel: (model: AvailableModel) => void;
  availableModels: readonly AvailableModel[];
};

/**
 * モデル選択の状態管理と localStorage 永続化を担うフック。
 * localStorage が利用不可（プライベートブラウジング等）でもクラッシュしない。
 */
export function useModelSelector(): UseModelSelectorResult {
  const [selectedModel, setSelectedModel] = useState<AvailableModel>(() => {
    // 初期値は localStorage から読み出し、不正値なら DEFAULT_MODEL にフォールバック
    try {
      const stored = window.localStorage.getItem(MODEL_STORAGE_KEY);
      if (stored !== null && (AVAILABLE_MODELS as readonly string[]).includes(stored)) {
        return stored as AvailableModel;
      }
    } catch {
      // localStorage 不可環境ではデフォルト値で起動する
    }
    return DEFAULT_MODEL;
  });

  const setModel = useCallback((model: AvailableModel) => {
    setSelectedModel(model);
    try {
      window.localStorage.setItem(MODEL_STORAGE_KEY, model);
    } catch {
      // 保存失敗はフェイルセーフ: セッション中は state で保持される
    }
  }, []);

  return {
    selectedModel,
    setModel,
    availableModels: AVAILABLE_MODELS,
  };
}
